import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ImageCarousel from '../../Categories/components/ImageCarousel';
import HierarchicalTreeSelector from '../../Common/components/HierarchicalTreeSelector';

const WHATSAPP_PHONE = '5551981738538';

// Recursive component to render specification values
const SpecificationValue = ({ value }) => {
  if (Array.isArray(value)) {
    return (
      <div className="text-sm font-semibold text-gray-800">
        {value.join(' × ')}
      </div>
    );
  }

  if (typeof value === 'object' && value !== null) {
    return (
      <div className="space-y-1">
        {Object.entries(value).map(([subKey, subValue]) => (
          <div key={subKey} className="flex justify-between">
            <span className="text-sm text-gray-700">
              {subKey.replace(/_/g, ' ').split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}:
            </span>
            <span className="text-sm font-semibold text-gray-800 ml-2">
              {typeof subValue === 'object' && subValue !== null
                ? JSON.stringify(subValue)
                : subValue?.toString()}
            </span>
          </div>
        ))}
      </div>
    );
  }

  if (typeof value === 'boolean') {
    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
        value ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
      }`}>
        {value ? 'Sim' : 'Não'}
      </span>
    );
  }

  if (typeof value === 'number') {
    return <span className="text-sm font-semibold text-gray-800">{value}</span>;
  }

  return <span className="text-sm text-gray-700">{value?.toString() || '-'}</span>;
};

const SubcategoryDetails = ({ subcategory, parent_type, parent, root_category, product_models, virtual_products = [], filter_values, breadcrumb_path = [], selected_model_id = null, categories = [] }) => {
  const [selectedModelId, setSelectedModelId] = useState(selected_model_id);
  const [selectedModel, setSelectedModel] = useState(selected_model_id ? product_models.find(m => m.id.toString() === selected_model_id.toString()) : null);
  const [expandedGroups, setExpandedGroups] = useState({});
  const [templateLabelMap, setTemplateLabelMap] = useState({});
  const [templateGroups, setTemplateGroups] = useState(null);

  const [filteredProducts, setFilteredProducts] = useState([]);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  const [selectedTreeItem, setSelectedTreeItem] = useState({
    id: subcategory.id,
    type: 'subcategory',
    item: subcategory,
    parentCategoryId: root_category?.id || parent?.id
  });

  useEffect(() => {
    const fetchTemplate = async () => {
      try {
        const response = await fetch(`/api/product_templates/merged?subcategory_id=${encodeURIComponent(subcategory.id)}`);
        if (response.ok) {
          const template = await response.json();

          const labelMap = {};
          if (Array.isArray(template) && template.length > 0) {
            setTemplateGroups(template);

            template.forEach(group => {
              if (group.fields && Array.isArray(group.fields)) {
                group.fields.forEach(field => {
                  if (field.key && field.label) {
                    labelMap[field.key] = field.label;
                  }
                });
              }
            });
          }
          setTemplateLabelMap(labelMap);
        }
      } catch (error) {
        console.error('Failed to fetch template:', error);
      }
    };

    fetchTemplate();
  }, [subcategory.id]);

  useEffect(() => {
    if (selectedModel && selectedModel.especificacoes) {
      const specs = groupSpecifications(selectedModel.especificacoes);
      const initialExpandedState = {};
      Object.keys(specs).forEach(group => {
        initialExpandedState[group] = true;
      });
      setExpandedGroups(initialExpandedState);
    }
  }, [selectedModel, templateGroups]);

  useEffect(() => {
    const handlePopState = (event) => {
      const pathParts = window.location.pathname.split('/');
      const modelIndex = pathParts.indexOf('model');

      if (modelIndex !== -1 && modelIndex + 1 < pathParts.length) {
        const modelId = pathParts[modelIndex + 1];
        const model = product_models.find(m => m.id.toString() === modelId);

        setSelectedModelId(modelId);
        setSelectedModel(model);

        if (model) {
          const specs = groupSpecifications(model.especificacoes);
          const initialExpandedState = {};
          Object.keys(specs).forEach(group => {
            initialExpandedState[group] = true;
          });
          setExpandedGroups(initialExpandedState);
        }
      } else {
        setSelectedModelId('');
        setSelectedModel(null);
        setExpandedGroups({});
      }
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [product_models]);

  const getImageUrl = (imagePath) => {
    if (!imagePath) {
      return '/assets/placeholder.jpg';
    }
    if (imagePath.startsWith('http://') || imagePath.startsWith('https://') || imagePath.startsWith('/rails/active_storage/')) {
      return imagePath;
    }
    if (imagePath.startsWith('/assets/')) {
      return imagePath;
    }
    return `/assets/${imagePath}`;
  };

  const filteredModels = filteredProducts.length > 0
    ? product_models.filter(model =>
        filteredProducts.some(vp => vp.id === model.id)
      )
    : product_models;

  const availableModels = filteredModels.map(model => ({
    id: model.id,
    name: model.name
  }));

  useEffect(() => {
    if (hasActiveFilters) {
      setSelectedModelId('');
      setSelectedModel(null);
    }
  }, [hasActiveFilters]);

  const handleModelChange = (e) => {
    const modelId = e.target.value;
    setSelectedModelId(modelId);

    if (modelId) {
      const model = product_models.find(m => m.id.toString() === modelId);
      setSelectedModel(model);

      const newUrl = `/subcategories/${subcategory.id}/model/${modelId}`;
      window.history.pushState({ path: newUrl }, '', newUrl);

      const specs = groupSpecifications(model.especificacoes);
      const initialExpandedState = {};
      Object.keys(specs).forEach(group => {
        initialExpandedState[group] = true;
      });
      setExpandedGroups(initialExpandedState);
    } else {
      setSelectedModel(null);
      setExpandedGroups({});

      const newUrl = `/subcategories/${subcategory.id}`;
      window.history.pushState({ path: newUrl }, '', newUrl);
    }
  };

  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => ({
      ...prev,
      [groupName]: !prev[groupName]
    }));
  };

  const isValidValue = (value) => {
    if (value === null || value === undefined || value === '') return false;
    if (Array.isArray(value) && value.length === 0) return false;
    if (typeof value === 'object' && !Array.isArray(value) && Object.keys(value).length === 0) return false;
    return true;
  };

  const groupSpecifications = (specs) => {
    if (templateGroups && templateGroups.length > 0) {
      const groups = {};

      templateGroups.forEach(templateGroup => {
        const groupName = templateGroup.title || 'Outros';
        groups[groupName] = {};

        if (templateGroup.fields && Array.isArray(templateGroup.fields)) {
          templateGroup.fields.forEach(field => {
            if (field.key && specs[field.key] !== undefined && isValidValue(specs[field.key])) {
              groups[groupName][field.key] = specs[field.key];
            }
          });
        }
      });

      const templateKeys = new Set();
      templateGroups.forEach(group => {
        if (group.fields) {
          group.fields.forEach(field => {
            if (field.key) templateKeys.add(field.key);
          });
        }
      });

      const ungroupedSpecs = {};
      Object.entries(specs).forEach(([key, value]) => {
        if (!templateKeys.has(key) && isValidValue(value)) {
          ungroupedSpecs[key] = value;
        }
      });

      if (Object.keys(ungroupedSpecs).length > 0) {
        groups['Outros'] = ungroupedSpecs;
      }

      return Object.fromEntries(
        Object.entries(groups).filter(([_, value]) => Object.keys(value).length > 0)
      );
    }

    const groups = {
      'Dimensões e Capacidade': {},
      'Especificações Técnicas': {},
      'Sistema e Controle': {},
      'Energia e Potência': {},
      'Outros': {}
    };

    Object.entries(specs).forEach(([key, value]) => {
      if (!isValidValue(value)) return;

      if (key.includes('area') || key.includes('dimensoes') || key.includes('espessura') ||
          key.includes('capacidade') || key.includes('peso')) {
        groups['Dimensões e Capacidade'][key] = value;
      }
      else if (key.includes('velocidade') || key.includes('precisao') || key.includes('aceleracao') ||
              key.includes('comprimento') || key.includes('diametro') || key.includes('faixa')) {
        groups['Especificações Técnicas'][key] = value;
      }
      else if (key.includes('sistema') || key.includes('controle') || key.includes('cabecote') ||
              key.includes('ressonador') || key.includes('modo')) {
        groups['Sistema e Controle'][key] = value;
      }
      else if (key.includes('potencia') || key.includes('tensao') || key.includes('motor') ||
              key.includes('chiller') || key.includes('estabilizador') || key.includes('compressor')) {
        groups['Energia e Potência'][key] = value;
      }
      else {
        groups['Outros'][key] = value;
      }
    });

    return Object.fromEntries(
      Object.entries(groups).filter(([_, value]) => Object.keys(value).length > 0)
    );
  };

  const getGroupIcon = (groupName) => {
    switch (groupName) {
      case 'Dimensões e Capacidade':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4"></path>
          </svg>
        );
      case 'Especificações Técnicas':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z"></path>
          </svg>
        );
      case 'Sistema e Controle':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
          </svg>
        );
      case 'Energia e Potência':
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 10V3L4 14h7v7l9-11h-7z"></path>
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
        );
    }
  };

  const buildWhatsappLink = (modelName) => {
    const message = `Olá! Gostaria de receber mais informações sobre o modelo *${modelName}*. Podem me ajudar?`;
    return `https://wa.me/${WHATSAPP_PHONE}?text=${encodeURIComponent(message)}`;
  };

  return (
    <div className="container mx-auto px-4 py-8">
        <div className="mb-6 flex items-center text-sm">
          <a href="/" className="text-orange-500 hover:text-orange-600">Home</a>
          <span className="mx-2 text-gray-500">/</span>

          <a href={`/?category=${root_category?.id || subcategory.category?.id || parent.id}`} className="text-orange-500 hover:text-orange-600">
            {root_category?.name || subcategory.category?.name || (parent_type === 'category' ? parent.name : 'Categorias')}
          </a>
          <span className="mx-2 text-gray-500">/</span>

          {breadcrumb_path.length > 0 && (
            <>
              {breadcrumb_path.map((item) => (
                <React.Fragment key={item.id}>
                  <a
                    href={`/?subcategory=${item.id}`}
                    className="text-orange-500 hover:text-orange-600"
                  >
                    {item.name}
                  </a>
                  <span className="mx-2 text-gray-500">/</span>
                </React.Fragment>
              ))}
            </>
          )}

          {breadcrumb_path.length === 0 && parent_type === 'subcategory' && (
            <>
              <a href={`/?subcategory=${parent.id}`} className="text-orange-500 hover:text-orange-600">
                {parent.name}
              </a>
              <span className="mx-2 text-gray-500">/</span>
            </>
          )}

          <span className="text-gray-700">{subcategory.name}</span>
        </div>

        <div className="flex md:flex-row flex-col gap-8">
          <div className="md:w-1/4 w-full">
            <HierarchicalTreeSelector
              categories={categories}
              selectedItem={selectedTreeItem}
              onSelectionChange={(selection) => {
                if (selection) {
                  if (selection.type === 'category') {
                    window.location.href = `/?category=${selection.id}`;
                  } else if (selection.type === 'subcategory') {
                    if (selection.item.is_leaf) {
                      if (selection.id === subcategory.id) {
                        return;
                      }
                      window.location.href = `/subcategories/${selection.id}`;
                    } else {
                      window.location.href = `/?subcategory=${selection.id}`;
                    }
                  }
                } else {
                  window.location.href = '/';
                }
              }}
              products={[]}
              onFilterChange={() => {}}
            />
          </div>

          <div className="md:w-3/4 w-full">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="p-6">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{subcategory.name}</h1>

                <div className="mb-6">
                  {(() => {
                    const hasModelImages = selectedModel?.image_urls && selectedModel.image_urls.length > 0;
                    const hasSubcategoryImages = subcategory.image_urls && subcategory.image_urls.length > 0;
                    const hasSubcategoryImage = subcategory.image_url;

                    if (hasModelImages) {
                      return (
                        <ImageCarousel
                          images={selectedModel.image_urls}
                          alt={selectedModel.name}
                          showThumbnails={true}
                          allowFullscreen={true}
                        />
                      );
                    } else if (hasSubcategoryImages) {
                      return (
                        <ImageCarousel
                          images={subcategory.image_urls}
                          alt={subcategory.name}
                          showThumbnails={true}
                          allowFullscreen={true}
                        />
                      );
                    } else if (hasSubcategoryImage) {
                      return (
                        <div className="h-80 bg-gray-200 rounded-lg">
                          <img
                            src={getImageUrl(subcategory.image_url)}
                            alt={subcategory.name}
                            className="w-full h-full object-contain"
                          />
                        </div>
                      );
                    } else {
                      return (
                        <div className="h-80 bg-gray-200 rounded-lg flex items-center justify-center">
                          <span className="text-gray-500">Sem imagem disponível</span>
                        </div>
                      );
                    }
                  })()}
                </div>

                <div className="prose max-w-none">
                  <p>{subcategory.description}</p>
                </div>
              </div>

              <div className="border-t border-gray-200 p-6">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Modelos Disponíveis</h2>

                {availableModels.length > 0 ? (
                  <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Modelo
                    </label>
                    <select
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-orange-500 focus:border-orange-500"
                      value={selectedModelId}
                      onChange={handleModelChange}
                    >
                      <option value="">Selecione um modelo</option>
                      {availableModels.map(model => (
                        <option key={model.id} value={model.id}>{model.name}</option>
                      ))}
                    </select>
                    {availableModels.length > 10 && (
                      <p className="text-sm text-gray-500 mt-1">
                        Role a lista acima para ver todos os modelos disponíveis ({availableModels.length} itens)
                      </p>
                    )}
                  </div>
                ) : (
                  <p className="text-gray-500">Não há modelos disponíveis com os filtros selecionados.</p>
                )}
              </div>

              {selectedModel && (
                <div className="border-t border-gray-200 p-6">
                  <div className="flex items-center mb-6">
                    <h2 className="ml-4 text-2xl font-bold text-gray-900">Modelo {selectedModel.name}</h2>
                  </div>

                  <div className="space-y-4 mb-8">
                    {Object.entries(groupSpecifications(selectedModel.especificacoes)).map(([groupName, specs]) => (
                      <div key={groupName} className="border border-gray-200 rounded-lg overflow-hidden">
                        <button
                          className={`w-full flex items-center justify-between px-4 py-3 text-left ${
                            expandedGroups[groupName] ? 'bg-orange-50' : 'bg-gray-50'
                          }`}
                          onClick={() => toggleGroup(groupName)}
                        >
                          <div className="flex items-center">
                            <span className={`flex-shrink-0 mr-2 ${expandedGroups[groupName] ? 'text-orange-500' : 'text-gray-500'}`}>
                              {getGroupIcon(groupName)}
                            </span>
                            <h3 className={`text-md font-semibold ${expandedGroups[groupName] ? 'text-orange-700' : 'text-gray-900'}`}>
                              {groupName}
                            </h3>
                          </div>
                          <svg
                            className={`w-5 h-5 transition-transform ${expandedGroups[groupName] ? 'transform rotate-180 text-orange-500' : 'text-gray-500'}`}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </button>

                        {expandedGroups[groupName] && (
                          <div className="divide-y divide-gray-200">
                            {Object.entries(specs).map(([key, value]) => {
                              const displayKey = templateLabelMap[key] || key.replace(/_/g, ' ').split(' ').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ');

                              const isNestedObject = typeof value === 'object' && value !== null && !Array.isArray(value);

                              return (
                                <div key={key} className="p-4">
                                  {isNestedObject ? (
                                    <>
                                      <div className="text-sm font-medium text-gray-900 mb-2">
                                        {displayKey}
                                      </div>
                                      <SpecificationValue value={value} />
                                    </>
                                  ) : (
                                    <div className="flex justify-between items-center">
                                      <div className="text-sm font-medium text-gray-900">
                                        {displayKey}
                                      </div>
                                      <div>
                                        <SpecificationValue value={value} />
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 flex justify-end">
                    <a
                      href={buildWhatsappLink(selectedModel.name)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white px-6 py-3 rounded-md transition-colors font-medium"
                    >
                      <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                      Solicitar mais informações via WhatsApp
                    </a>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
  );
};

SubcategoryDetails.propTypes = {
  subcategory: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired,
    filter_type: PropTypes.string
  }).isRequired,
  parent_type: PropTypes.oneOf(['category', 'subcategory']).isRequired,
  parent: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  }).isRequired,
  root_category: PropTypes.shape({
    id: PropTypes.number.isRequired,
    name: PropTypes.string.isRequired
  }),
  product_models: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ).isRequired,
  virtual_products: PropTypes.array,
  filter_values: PropTypes.arrayOf(PropTypes.string),
  breadcrumb_path: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number.isRequired,
      name: PropTypes.string.isRequired
    })
  ),
  selected_model_id: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number
  ])
};

SpecificationValue.propTypes = {
  value: PropTypes.any
};

export default SubcategoryDetails;
